import React, { useEffect, useState } from 'react'
import { useHistory, NavLink, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { motion } from 'framer-motion'
import { useCookies } from 'react-cookie'
import { useGoogleLogin } from '@react-oauth/google'
import useStorage from 'containers/useStorage'

import InputField from '../../components/InputField'
import { selectToken } from '../../containers/authentication/authenticationSlice'
import useSignIn from '../../containers/authentication/useSignIn'

import * as rudderanalytics from 'rudder-sdk-js'

import mayaLogo from 'images/maya-logo.png'

import './SignIn.scss'
import usePageTitle from 'containers/pageTitle/usePageTitle'
import ScreenLoading from 'components/ScreenLoading'
import useMicrosoft from 'containers/authentication/useMicrosoft'
import PopUpMessage from 'components/PopUpMessage'
import TagManager from 'react-gtm-module'

const SignIn = () => {
  const token = useSelector(selectToken)
  const [cookies] = useCookies(['user_id'])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loadingSignIn, setLoadingSignIn] = useState(false)
  let history = useHistory()
  const location = useLocation()
  const storage = useStorage()

  const {
    signInError,
    removeError,
    signInWithGoogle,
    signInWithEmailPassword,
  } = useSignIn()

  //Page title
  usePageTitle('Sign In - Maya');

  useEffect(() => {
    // Clear any stored redirect URL if user manually navigates to sign-in
    // This prevents redirect loops
    if (window.location.pathname === '/sign-in') {
      storage.removeUrlWhenLoggedIn()
    }
  }, [])

  useEffect(() => {
    if (signInError?.details?.type === "merge_required" || signInError?.details?.type === "no_password_set") {
      history.push({
        pathname: `merge-account`,
        search: `?email=${signInError.details.email}&provider=${signInError.details.provider}&is_google_signin=${signInError.details.is_google_signin}&is_microsoft_signin=${signInError.details.is_microsoft_signin}&is_password_signin=${signInError.details.is_password_signin}`,
        state: {
          googleAccessToken: signInError.details.google_id_token,
        }
      })
    }
  }, [signInError, history])

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (!(await signInWithGoogle({ google_access_token: tokenResponse.access_token }))) {
        history.push({
          pathname: '/sign-up',
          // Keep the query string (return_to etc.) — a merchant mid Shopify
          // install must resume the connect after signing up, not land on '/'.
          search: location.search,
          state: {
            googleAccessToken: tokenResponse.access_token,
          },
        })
      }
    },
    onError: () => {
      console.log('Google sign in failed')
    },
  })

  const {getAuthUri} = useMicrosoft()

  const handleSignInWithMicrosoft = async () => {
    const response = await getAuthUri()
    if (response) {
      window.location.href = response.auth_uri
    }
    // handle error
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    setLoadingSignIn(true)
    await signInWithEmailPassword({ email, password })
    TagManager.dataLayer({ dataLayer: { event: 'sign_in_completed', is_custom_track: true }})
    // rudderanalytics.track('sign_in_completed')
  }

  // once the user is signed in we check for mcp_auth_id and next params
  if (token && cookies.user_id && token.userId === parseInt(cookies.user_id)) {
    const searchParams = new URLSearchParams(location.search)
    const mcpAuthToken = searchParams.get('mcp_auth_token')
    const nextUrl = searchParams.get('next')
    const returnTo = searchParams.get('return_to')

    if (mcpAuthToken && nextUrl) {
      // Redirect to organization selection page with the MCP auth parameters
      const organizationSelectUrl = `/organization-select?mcp_auth_token=${mcpAuthToken}&next=${encodeURIComponent(nextUrl)}`
      history.push(organizationSelectUrl)
      return null
    }

    // check for return_to query param (used by shopify integration and other flows)
    if (returnTo) {
      window.location.href = decodeURIComponent(returnTo)
      return null
    }

    const url = storage.getUrlWhenLoggedIn()
    storage.removeUrlWhenLoggedIn()
    if (url) {
      window.location.href = url
      return null
    }

    window.location.href = '/'
    return null
  }

  const redirectToSignUp = () => {
    // Keep the query string (return_to etc.) — a merchant mid Shopify install
    // must resume the connect after signing up, not land on '/'.
    history.push({ pathname: '/sign-up', search: location.search })
  }

  return (
    <div className="page-column">
      <header>
        <div className="logo">
          <img src={mayaLogo} alt="Maya" />
        </div>
      </header>
      <div className="container-grow">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="container-column sign-in-box"
        >
          <div className="title-container">
            <h1 className="text-align-center">Sign In</h1>
            <div className="secondary-text text-align-center">
              If you have not created an account yet, {' '}
              <button className="link" onClick={redirectToSignUp}>
                sign up.
              </button>
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="container-grow container-column sign-in"
            noValidate
          >
            <div onClick={googleLogin} className="with-google" style={{marginBottom: "10px"}}>
              Sign in with Google
            </div>
            <div onClick={handleSignInWithMicrosoft} className="with-microsoft">
              Sign in with Microsoft
            </div>
            <div className="or">OR</div>
            <div className="container-column width-100 credentials">
              <InputField
                value={email}
                setValue={setEmail}
                error={signInError.email}
                removeError={() => removeError('email')}
                label="Email"
                type="email"
                icon="email"
                placeholder="you@example.com"
                required
              />
              <InputField
                value={password}
                setValue={setPassword}
                error={signInError.password}
                removeError={() => removeError('password')}
                label="Password"
                type="password"
                icon="lock"
                placeholder="Enter your password"
                required
              />
              <NavLink to="/forgot-password" className="form-link">
                Forgot Password?
              </NavLink>
            </div>
            {signInError.server ? (
              <span className="server-error">{signInError.server}</span>
            ) : location.state?.microsoftError && (
              <span className="server-error">{location.state.microsoftError}</span>
            )}
            <button type="submit" className="submit-button">
              {loadingSignIn ? (
                <ScreenLoading spinnerColor="white" />
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default React.memo(SignIn)
